/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const chatwootBaseUrl = process.env.REACT_APP_CHATWOOT_BASE_URL;
const chatwootToken = process.env.REACT_APP_CHATWOOT_TOKEN;

/**
 * Gate live-chat init on route. Public `/pay/*` pages are an
 * isolated checkout surface — the Chatwoot bubble would be an
 * uninvited third-party widget over a sensitive "confirm
 * payment" form.
 *
 * When the user lands cold on /pay/* we skip the SDK injection
 * entirely (no script tag, no network request, no bubble). When
 * they navigate IN to /pay/* after Chatwoot has already loaded
 * (e.g. / → /pay/p/abc) we toggle the bubble's visibility off; on
 * navigation OUT we toggle it back on so the widget stays available
 * everywhere else.
 *
 * Note: requires this component to be rendered inside <Router> so
 * `useLocation` works. See App.js where we moved it inside for
 * exactly this reason.
 */
const isPayRoute = (pathname) =>
  typeof pathname === 'string' && pathname.startsWith('/pay/');

function Livechat() {
  const location = useLocation();
  const sdkLoadedRef = useRef(false);

  // One-shot SDK injection, only on non-pay routes. Once loaded,
  // the SDK stays on `window.chatwootSDK` for the life of the tab;
  // re-injecting on every route change would spam the document
  // with duplicate <script> tags. The flag guards against that.
  useEffect(() => {
    if (sdkLoadedRef.current) return;
    if (isPayRoute(location.pathname)) return;

    window.chatwootSettings = {
      hideMessageBubble: false,
      position: 'right',
      locale: 'en',
      type: 'standard',
    };

    sdkLoadedRef.current = true;

    (function (d, t) {
      const BASE_URL = chatwootBaseUrl;
      const g = d.createElement(t),
        s = d.getElementsByTagName(t)[0];
      g.src = BASE_URL + '/packs/js/sdk.js';
      g.defer = true;
      g.async = true;
      s.parentNode.insertBefore(g, s);

      g.onload = function () {
        window.chatwootSDK.run({
          websiteToken: chatwootToken,
          baseUrl: BASE_URL,
        });
      };
    })(document, 'script');
  }, [location.pathname]);

  // Bubble-visibility toggle on route change. Fires on every
  // navigation; no-op if the SDK hasn't loaded yet. The SDK's
  // `$chatwoot` object becomes available after the async script
  // finishes — a user who lands on / and immediately clicks into
  // /pay/ might race ahead of the load, so we check-and-skip.
  useEffect(() => {
    const onPay = isPayRoute(location.pathname);
    // `$chatwoot` is the SDK's public runtime object, populated
    // once `run()` completes inside the script's onload callback.
    const api = typeof window !== 'undefined' ? window.$chatwoot : null;
    if (!api) return;
    try {
      api.toggleBubbleVisibility(onPay ? 'hide' : 'show');
    } catch {
      // SDK may reject before it's fully initialized — harmless,
      // the initial-render state from chatwootSettings covers it.
    }
  }, [location.pathname]);

  return null;
}

export default Livechat;
